import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

/**
 * SignatureCanvas - Modal içinde imza çizim componenti
 * 
 * Özellikler:
 * - react-signature-canvas ile çizim
 * - Mavi kalem rengi (gerçek mavi kalem rengi)
 * - Base64 formatında kayıt
 * - Temizleme ve onaylama butonları
 */
const SignatureCanvasModal = ({
  open,
  value,
  onClose,
  onSave,
  question,
}) => {
  const signatureRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // Modal açıldığında mevcut imzayı yükle
  useEffect(() => {
    if (open && signatureRef.current && value) {
      // Eğer value base64 image ise, canvas'a yükle
      if (typeof value === 'string' && value.startsWith('data:image')) {
        const img = new Image();
        img.onload = () => {
          const canvas = signatureRef.current.getCanvas();
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // Mevcut imza yüklendiğinde isEmpty false olmalı
          setIsEmpty(false);
        };
        img.src = value;
      }
    } else if (open && signatureRef.current) {
      // Modal açıldığında canvas'ı temizle (yeni imza için)
      signatureRef.current.clear();
      setIsEmpty(true);
    }
  }, [open, value]);

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setIsEmpty(true);
    }
  };

  const handleCanvasEnd = () => {
    // Çizim bittiğinde isEmpty durumunu güncelle
    if (signatureRef.current) {
      setIsEmpty(signatureRef.current.isEmpty());
    }
  };

  const handleSave = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      // Base64 formatında kaydet (PNG formatında)
      const dataURL = signatureRef.current.toDataURL('image/png');
      if (onSave) {
        onSave(dataURL);
      }
      if (onClose) {
        onClose();
      }
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>
        {question?.displayText || 'İmza At'}
        {question?.required && <span style={{ color: 'red' }}> *</span>}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Lütfen aşağıdaki alana imzanızı çizin
          </Typography>
          <Box
            sx={{
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              bgcolor: 'background.paper',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                width: 600,
                height: 200,
                className: 'signature-canvas',
              }}
              backgroundColor="#ffffff"
              penColor="#0066cc" // Gerçek mavi kalem rengi
              onEnd={handleCanvasEnd}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            size="small"
          >
            Temizle
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>İptal</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<CheckIcon />}
          disabled={isEmpty}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SignatureCanvasModal.propTypes = {
  open: PropTypes.bool.isRequired,
  value: PropTypes.string, // Base64 image string
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  question: PropTypes.object,
};

SignatureCanvasModal.defaultProps = {
  value: null,
  question: null,
};

export default SignatureCanvasModal;

